
import React, { createContext, useState, useEffect, useCallback } from 'react'
import axiosInstance from '../utils/axiosInstance'
import { API_ENDPOINTS } from '../constants/config'

export const FeatureFlagContext = createContext({
  features: {},
  loading: true,
  error: null,
  isFeatureEnabled: () => false,
  hasAnyFeature: () => false,
  refreshFeatures: () => Promise.resolve(),
})

export function FeatureFlagProvider({ children }) {
  const [features, setFeatures] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [schoolId, setSchoolId] = useState(null)

  // Get school ID from user data
  const getSchoolId = useCallback(() => {
    try {
      const userStr = localStorage.getItem('user') || sessionStorage.getItem('user')
      if (!userStr) return null
      
      const user = JSON.parse(userStr)
      const id = user?.school && (typeof user.school === 'object' ? user.school._id : user.school)
      return id
    } catch (err) {
      console.error('Error parsing user data:', err)
      return null
    }
  }, [])

  const hasAuthContext = useCallback(() => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token')
    const currentSchoolId = getSchoolId()
    return Boolean(token && currentSchoolId)
  }, [getSchoolId])

  // Fetch feature flags from backend
  const fetchFeatures = useCallback(async () => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token')
    const currentSchoolId = getSchoolId()
    
    if (!token || !currentSchoolId) {
      console.log('[FEATURE_CONTEXT] Skipping fetch: auth context missing (token/schoolId)')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      const response = await axiosInstance.get(
        API_ENDPOINTS.schoolFeatures(currentSchoolId)
      )

      console.log('=== FEATURE CONTEXT API RESPONSE ===')
      console.log('[FEATURE_CONTEXT] Full API Response:', response.data)
      console.log('[FEATURE_CONTEXT] Response data:', response.data?.data)
      console.log('[FEATURE_CONTEXT] Features in response:', response.data?.data?.features)
      
      if (response.data?.data?.features) {
        const featuresData = response.data.data.features
        console.log('[FEATURE_CONTEXT] Features object type:', typeof featuresData)
        console.log('[FEATURE_CONTEXT] Features keys:', Object.keys(featuresData))
        console.log('[FEATURE_CONTEXT] Enabled features:', Object.entries(featuresData)
          .filter(([_, v]) => v?.enabled === true)
          .map(([k]) => k))
        console.log('[FEATURE_CONTEXT] Sample features:', Object.entries(featuresData).slice(0, 5))
        setFeatures(featuresData)
      } else {
        // If no features in response, initialize with empty object
        console.error('[FEATURE_CONTEXT] ❌ NO FEATURES IN API RESPONSE!')
        console.error('[FEATURE_CONTEXT] Response structure:', JSON.stringify(response.data, null, 2))
        setFeatures({})
      }
      
      setSchoolId(currentSchoolId)
    } catch (err) {
      console.error('Error fetching feature flags:', err)
      setError(err.message || 'Failed to load feature flags')
      
      // On error, fall back to empty features (show nothing)
      setFeatures({})
    } finally {
      setLoading(false)
    }
  }, [getSchoolId])

  // Initial fetch on mount
  useEffect(() => {
    fetchFeatures()
  }, [fetchFeatures])

  // Refetch when auth is written after login (provider remains mounted)
  useEffect(() => {
    const tryRefetchAfterAuth = () => {
      if (hasAuthContext()) {
        console.log('[FEATURE_CONTEXT] auth-changed detected, refetching features')
        fetchFeatures()
      } else {
        // logout happened — clear stale school features immediately
        setFeatures({})
        setSchoolId(null)
        setLoading(false)
      }
    }

    window.addEventListener('auth-changed', tryRefetchAfterAuth)
    window.addEventListener('storage', tryRefetchAfterAuth)

    return () => {
      window.removeEventListener('auth-changed', tryRefetchAfterAuth)
      window.removeEventListener('storage', tryRefetchAfterAuth)
    }
  }, [fetchFeatures, hasAuthContext])

  // Refresh when school changes
  useEffect(() => {
    const currentSchoolId = getSchoolId()
    if (currentSchoolId && currentSchoolId !== schoolId) {
      fetchFeatures()
    }
  }, [getSchoolId, schoolId, fetchFeatures])

  // Check if a feature is enabled
  const isFeatureEnabled = useCallback((featureKey) => {
    if (!featureKey || !features) return false
    
    const feature = features[featureKey]
    if (!feature) return false
    
    return feature.enabled === true
  }, [features])

  // Check if any of the provided features are enabled
  const hasAnyFeature = useCallback((featureKeys) => {
    if (!Array.isArray(featureKeys)) return false
    return featureKeys.some(key => isFeatureEnabled(key))
  }, [isFeatureEnabled])

  // Manual refresh function
  const refreshFeatures = useCallback(async () => {
    await fetchFeatures()
  }, [fetchFeatures])

  const value = {
    features,
    loading,
    error,
    isFeatureEnabled,
    hasAnyFeature,
    refreshFeatures,
  }

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  )
}
